import React, { useEffect, useState } from "react";

import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Autocomplete, Box, Button, Popover, Stack, TextField, TextFieldProps } from "@mui/material";
import { clientTypeOptions } from "constants/index";
import { ClientType, IClientFilters } from "store/types/user-management";

const commonInputProps: {
  sx?: TextFieldProps["sx"];
  size: TextFieldProps["size"];
  InputLabelProps: TextFieldProps["InputLabelProps"];
} = {
  size: "small",
  InputLabelProps: { shrink: true }
};

interface IFiltersProps {
  queries: IClientFilters;
  onChange: (partial: Partial<IClientFilters>) => void;
  onFilterToggle: (open: boolean) => void;
}

export const Filters: React.FC<IFiltersProps> = ({ queries, onChange, onFilterToggle }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Local popover state seeded from the latest queries prop.
  const [selectedType, setSelectedType] = useState<ClientType | null>(
    queries.type ? (queries.type as ClientType) : null
  );
  const [selectedRm, setSelectedRm] = useState<string | null>(queries.rm ? (queries.rm as string) : null);

  // Fix #3: Sync local popover state whenever the parent's queries change
  // (e.g. after a create, filter, or search).  This prevents the popover
  // from showing stale values that do not reflect the currently active
  // filter state.
  useEffect(() => {
    setSelectedType(queries.type ? (queries.type as ClientType) : null);
    setSelectedRm(queries.rm ? (queries.rm as string) : null);
  }, [queries.type, queries.rm]);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    onFilterToggle(true);
  };

  // Fix #6: Reset local state whenever the popover closes so that a
  // subsequent open always reflects the current active filters rather
  // than whatever the user previously selected but didn't apply.
  const handleClose = () => {
    setSelectedType(queries.type ? (queries.type as ClientType) : null);
    setSelectedRm(queries.rm ? (queries.rm as string) : null);
    setAnchorEl(null);
    onFilterToggle(false);
  };

  const handleApply = () => {
    onChange({
      type: selectedType ?? undefined,
      rm: selectedRm ?? undefined
    });
    handleClose();
  };

  const handleCancel = () => {
    setSelectedType(queries.type ? (queries.type as ClientType) : null);
    setSelectedRm(queries.rm ? (queries.rm as string) : null);
    handleClose();
  };

  const isFiltersSelected = Boolean(selectedType || selectedRm);

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Button
        onClick={handleClick}
        variant="outlined"
        size="sm"
        startIcon={<FilterAltIcon />}
        sx={{ backgroundColor: isFiltersSelected ? "#dae0e2" : "transparent" }}
      >
        Filters
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack sx={{ p: "16px", gap: "12px", minWidth: "280px" }}>
          <Autocomplete
            options={clientTypeOptions}
            value={clientTypeOptions.find((option) => option.value === selectedType) ?? null}
            getOptionLabel={(option) => option.label}
            renderOption={(props, option) => <li {...props}>{option.label}</li>}
            onChange={(_, val) => setSelectedType(val ? val.value : null)}
            renderInput={(params) => (
              <TextField {...params} {...commonInputProps} label="Type" placeholder={selectedType || "Select"} />
            )}
          />

          {/* RM is a free-text partial match on the list endpoint (no rm-list source). */}
          <TextField
            {...commonInputProps}
            label="RM"
            placeholder="Search by RM"
            value={selectedRm ?? ""}
            onChange={(event) => setSelectedRm(event.target.value || null)}
          />

          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
            <Button size="sm" variant="outlined" sx={{ minWidth: "53px" }} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="contained"
              sx={{ backgroundColor: "#071F35", minWidth: "46px" }}
              onClick={handleApply}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Box>
  );
};
